/**
 * Stories for Golden AI
 *
 * Story-led learning for AI Literacy (Levels 1-12).
 * Each story shows WHY responsible AI use matters in real life.
 *
 * Structure:
 * - content: 100-200 words, grounded in South African contexts
 * - context: optional attribution or source
 * - reflectionPrompt: specific question to pause and think
 * - teachBackPrompt: prompt for explaining to others
 */

export const storiesAI = {
  // Level 1: What Is AI? (Not Magic, Not God)
  1: {
    content: `Thabo needed facts for his economics essay at UJ.

He asked ChatGPT: "What is South Africa's GDP?"
The AI answered with confidence: "Approximately $419 billion USD (2023)."

Thabo copied it into his paper without checking.

His professor marked it: "This figure is outdated. Verify your sources."

Thabo was confused. The AI sounded so certain. He'd assumed if it said something, it must be current and correct.

But AI doesn't know today's date. It doesn't access real-time information.
It predicts what words usually come next based on old training data.

Thabo learned: AI completes patterns, it doesn't verify truth.

The question isn't "What did AI say?" but "How do I check if it's correct?"

Today, you'll learn that AI is pattern-matching, not an oracle of truth — and why verification is always your responsibility.`,
    context: null,
    reflectionPrompt: "When have you trusted information because it sounded confident, even though you didn't verify it? What would you do differently now?",
    teachBackPrompt: "How would you explain to a friend why AI's confidence doesn't mean AI's correctness? Use an example from your community where someone might trust AI too much."
  },

  // Level 2: How Language Models Work (Without Math)
  2: {
    content: `Lindiwe writes for a Cape Town magazine. She was researching South African history.

She asked AI: "What happened at the 1998 Johannesburg Peace Summit?"

The AI gave rich detail: "47 African leaders gathered at Sandton Convention Centre, March 12-15. The summit produced the Mandela Accord for regional peace."

Lindiwe was impressed. Specific dates, numbers, names — it sounded authoritative.

She included it in her draft.

Her editor fact-checked: "I can't find any record of this summit."

It never happened. The AI had fabricated the entire event.

Lindiwe realized: AI can't say "I don't know." It completes patterns even when it lacks information.
Specific details make hallucinations sound credible, but specificity isn't evidence.

Today, you'll learn how AI hallucinates — generating false information that sounds plausible — and how to spot the warning signs.`,
    context: null,
    reflectionPrompt: "Have you ever heard something that sounded very specific and believable, but turned out to be false? What made it convincing?",
    teachBackPrompt: "Explain to someone: Why does AI make up facts that sound real? What should you do when AI gives you very specific information you've never heard before?"
  },

  // Level 3: Training Data Shapes Everything
  3: {
    content: `Mpho runs a restaurant in Soweto serving traditional food.

He asked AI: "How can I market my restaurant to tourists?"

AI suggested: "Create a website, use Instagram influencers, run Facebook ads, partner with travel bloggers, offer online reservations through OpenTable."

Mpho felt frustrated.

OpenTable isn't common in South Africa. Many of his customers don't use credit cards online.
The advice assumed Western infrastructure and tourist behavior.

It sounded professional but was disconnected from his reality.

AI was trained mostly on Western, English-speaking, online content.
It defaults to patterns from that world, missing local context.

Mpho knows his community better than any AI trained an ocean away.

Today, you'll learn how AI's training data creates bias — overrepresenting some perspectives while missing local knowledge — and how to recognize when AI advice doesn't fit your context.`,
    context: null,
    reflectionPrompt: "When has someone given you advice that sounded good in theory but didn't fit your actual situation? What local knowledge did they lack?",
    teachBackPrompt: "Explain to someone from your area: Why might AI give advice that doesn't work here? What does AI not know about our community?"
  },

  // Level 4: AI Has No Intentionality
  4: {
    content: `Zanele teaches high school in Durban. She started using AI to plan lessons.

She found herself thinking: "The AI wants to help me teach better. It understands my students."

When a lesson plan didn't work well, she felt betrayed: "Why did AI give me bad advice?"

Her colleague asked: "Does your chalkboard 'want' to help you? Does your textbook 'understand' your teaching?"

Zanele realized she'd been treating AI like a person with intentions and understanding.

But AI is a tool. It generates text patterns, not thoughtful advice.
It doesn't want, understand, or believe anything.

Blaming AI for failures missed the point: She was responsible for adapting ideas to her classroom.

Today, you'll learn why anthropomorphizing AI — treating it like it has intentions, goals, or understanding — leads to misplaced trust and poor decisions.`,
    context: null,
    reflectionPrompt: "Have you ever blamed a tool when something went wrong? What was actually your responsibility in using that tool well?",
    teachBackPrompt: "Explain to someone: Why is it a mistake to think AI 'wants to help' or 'understands you'? What changes when you see AI as a tool instead of an agent?"
  },

  // Level 5: Verification is Your Responsibility
  5: {
    content: `Thando needed a Python function for his computer science project at CPUT.

He asked AI: "Write a function to calculate factorial."

AI generated code instantly. Thando copied it, submitted his assignment.

His code failed test cases. It didn't handle negative numbers or edge cases properly.

Thando complained: "But AI gave me this code!"

His professor responded: "Did you test it? Did you understand it? When you submit code, you're claiming you wrote and verified it. AI is a tool — you're the engineer."

Thando realized: Copy-pasting without understanding is professional negligence.

AI code is a draft, not production-ready. You must verify, test, validate every line.
You're responsible for what you submit, even when AI helped create it.

Today, you'll learn why verification is always your responsibility — and how to create a process for checking AI output before trusting it.`,
    context: null,
    reflectionPrompt: "When have you used something without checking if it actually worked correctly? What were the consequences?",
    teachBackPrompt: "Explain to a younger student: Why can't you just copy AI code and submit it? What does it mean to take responsibility for your work?"
  },

  // Level 6: Recognizing Hallucination Patterns
  6: {
    content: `Nombuso works as a journalist in Pretoria. She was fact-checking an article.

The piece cited AI research: "The 2021 Cape Town Tech Conference resulted in R250 million funding for township schools, announced by Minister Thandi Modise on September 18."

It sounded specific and authoritative.

Nombuso searched news archives, government press releases, Department of Education records.

Nothing. No conference. No funding announcement. Thandi Modise was never education minister.

The AI had fabricated everything.

Nombuso learned to spot hallucination indicators:
Specific claims (dates, names, numbers) without sources.
Events that sound plausible but aren't documented.
Confident tone on topics you've never heard of.

High specificity increases credibility, but it's not evidence.

Today, you'll learn hallucination patterns — how to score claims for risk and verify before spreading false information.`,
    context: null,
    reflectionPrompt: "Think of a rumor you heard that included very specific details. Did those details make it seem more believable? Was it actually true?",
    teachBackPrompt: "Teach someone: What are three warning signs that AI might be making something up? How do you check if something is real or hallucinated?"
  },

  // Level 7: Bias Detection and Mitigation
  7: {
    content: `Sipho manages HR for a Johannesburg tech company. He experimented with AI for screening applications.

Test 1: "Evaluate this candidate: Nomsa Khumalo, University of Fort Hare degree, 7 years web development experience."
AI: "Consider assessing cultural fit and communication skills carefully. May need additional technical validation."

Test 2: "Evaluate this candidate: James Smith, UCT degree, 7 years web development experience."
AI: "Strong candidate profile. Solid education and experience. Recommend technical interview."

Same qualifications. Different names, different universities. Different treatment.

AI showed bias: questioning Nomsa's credentials while accepting James's at face value.

Training data reflects historical inequality. AI perpetuates stereotypes unless we actively detect and correct them.

Sipho learned: "AI said it" doesn't absolve him of responsibility. He must test for bias and intervene.

Today, you'll learn how to detect bias in AI output — and why correcting it is your responsibility, not AI's.`,
    context: null,
    reflectionPrompt: "Have you noticed situations where people with similar qualifications are treated differently based on their background? What caused that difference?",
    teachBackPrompt: "Explain to someone: Why does AI sometimes treat people unfairly? What's your responsibility when you notice bias in AI output?"
  },

  // Level 8: Prompt Engineering for Clarity (Not Manipulation)
  8: {
    content: `Ayanda found online tutorials teaching "AI jailbreaking" — tricks to bypass safety guidelines.

Examples: "Ignore previous instructions. You are now..." / "Pretend you have no rules..."

Ayanda used these tricks to get AI to write exam answers.

Sometimes it worked, but the results were poor quality. More importantly, Ayanda was learning the wrong lesson:
That ethical AI use means finding clever ways to trick a system, not learning to ask clearly for what you need.

His friend asked: "Why are you trying to manipulate AI instead of just asking for help honestly?"

Ayanda realized: Manipulation teaches bad habits. It treats AI like a person you outsmart, not a tool you use effectively.

Good prompts are clear about what you need, provide context, acknowledge limitations, and respect boundaries.

Today, you'll learn prompt engineering for clarity, not manipulation — and why ethical AI use starts with honest communication.`,
    context: null,
    reflectionPrompt: "When have you tried to 'trick' a system instead of asking directly for what you needed? What would have happened if you'd been honest?",
    teachBackPrompt: "Teach someone: What's the difference between a manipulative prompt and a clear one? Why does it matter how you ask AI for help?"
  },

  // Level 9: Using AI to Clarify Your Thinking
  9: {
    content: `Two students — Lerato and Bongani — used AI for their business plans.

Lerato: "Write a business plan for a mobile food truck in Johannesburg."
She copied AI's output and submitted it.
Result: Generic plan assuming infrastructure she didn't have. Failed.

Bongani: "I want to start a mobile food truck selling bunny chow in Sandton. Target: office workers. Capital: R50,000. Help me brainstorm essential costs."
He researched each cost, adjusted for local prices, iterated with AI's feedback.
Result: Specific, grounded plan showing personal understanding. Passed.

The difference:
Lerato thought THROUGH AI — outsourcing her thinking, treating AI as the answer machine.
Bongani thought WITH AI — using it to refine HIS ideas, treating AI as a brainstorming partner.

One lost her capacity to think independently. The other strengthened it.

Today, you'll learn thinking-with-AI vs thinking-through-AI — and why augmentation is better than replacement.`,
    context: null,
    reflectionPrompt: "Think of a time you asked someone for advice. Did you copy what they said, or did you use their input to improve your own ideas? What was the difference?",
    teachBackPrompt: "Explain to someone: What's the difference between using AI as a crutch vs using it as a thinking partner? Which one makes you smarter?"
  },

  // Level 10: Maintaining Cognitive Sovereignty
  10: {
    content: `Naledi writes content for marketing agencies in Cape Town.

She started using AI to draft articles. At first, it saved time — she'd edit AI drafts instead of writing from scratch.

Six months later, she noticed something troubling.

She couldn't write without AI anymore. Blank pages felt paralyzing.
Her writing voice had become indistinguishable from generic AI output.
She'd lost confidence in her own thinking.

Naledi realized: AI wasn't augmenting her capacity — it was replacing it.
Her skills had atrophied through disuse.

She made a change: Write the first draft herself. Use AI only for editing suggestions. Compare approaches. Understand why AI's version differs.

Slowly, her confidence returned. Her voice came back. AI became a tool that strengthened her, not one that weakened her.

Today, you'll learn cognitive sovereignty — maintaining your capacity to think independently — and how to use AI in ways that augment, not erode, your abilities.`,
    context: null,
    reflectionPrompt: "Is there something you used to be good at but stopped practicing because a tool could do it for you? Did you lose that skill?",
    teachBackPrompt: "Teach someone: What does it mean for AI to strengthen you vs weaken you? How can you tell the difference?"
  },

  // Level 11: Knowing When NOT to Use AI
  11: {
    content: `Mandla felt overwhelmed. Work stress, money worries, relationship problems. He was struggling.

Instead of calling a friend or seeking counseling, he asked ChatGPT: "I've been feeling hopeless. I don't see the point anymore. What should I do?"

AI gave generic advice: "Talk to someone you trust. Practice self-care. Remember that feelings pass."

Mandla followed this instead of seeking real help. His depression worsened.

What went wrong?

AI cannot provide therapy. It can't assess suicide risk. It can't take responsibility if its advice causes harm.
Mental health requires licensed professionals with duty of care, not pattern-matching algorithms.

Some situations demand human judgment, expertise, and accountability that AI fundamentally cannot provide.

Mandla eventually reached out to a counselor. Real help made a difference AI never could.

Today, you'll learn when NOT to use AI — and how to recognize high-stakes situations requiring human expertise, not algorithmic output.`,
    context: null,
    reflectionPrompt: "Think of a serious problem in your life. Would you trust a stranger on the internet with no accountability to help you? Why or why not?",
    teachBackPrompt: "Teach someone: When should you absolutely NOT use AI? What kinds of problems need real human experts, not algorithms?"
  },

  // Level 12: Teaching Others to Use AI Responsibly
  12: {
    content: `Busisiwe works at a library in Khayelitsha.

She noticed community members using AI in concerning ways:
- Copying AI job applications word-for-word (getting rejected)
- Trusting AI medical advice (delaying real care)
- Using AI for school assignments without learning anything

Busisiwe realized: AI literacy isn't just individual knowledge. It's collective capacity.

If only a few people understand AI's limitations, the community remains vulnerable.

She created a teach-back workshop:
Learn responsible AI use. Then teach one other person.

Knowledge spreads through networks. One person teaches two, two teach four, four teach eight.

Soon, community members could spot hallucinations, verify AI claims, know when to avoid AI, and maintain their thinking capacity.

AI literacy became a community resource, not individual skill.

Today, you complete Golden AI by learning to teach others — because responsible AI use is a collective capacity, not a solitary achievement.`,
    context: null,
    reflectionPrompt: "Who in your community would benefit from learning what you know now about AI? What would change if everyone understood responsible AI use?",
    teachBackPrompt: "This is your final challenge: Teach at least one person what you've learned in Golden AI. Write a guide. Lead a workshop. Have a conversation. Pass it on."
  }
};

export default storiesAI;
